import { ObservationDate } from "../development/ObservationCard";
/**
 * MobileReflectionFlow — Phase 3B
 * --------------------------------------------------------------
 * Mobile-first, one-question-at-a-time reflection experience for the
 * existing V1 evaluation templates. Pure presentation layer — no backend
 * change, no template change, no metric change, no logic change.
 *
 * What this component does
 *   • Renders one hero card per question.
 *   • Auto-advances after a rating tap (configurable delay).
 *   • Groups questions by inferred section_type (development / status /
 *     reflection) using the V1 `section` text and the question's metric
 *     and type. The actual question order from the backend is preserved.
 *   • Swedish micro-copy adapted by age_group (U8-U11 / U12-U15 / U16+).
 *   • Calls the same submit handler signature as the old form
 *     (onSubmit(evaluationId, responsesArray, templateRevision)).
 *
 * What this component intentionally does NOT do
 *   • Does NOT modify question text, metric, scale, ordering, validation,
 *     or any conditional-question logic from the backend.
 *   • Does NOT call any new endpoint.
 *   • Does NOT aggregate, score, rank, or visualise data.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  ArrowLeft,
  ArrowRight,
  Check,
  Sparkles,
  Heart,
  PenLine,
  Megaphone,
} from "lucide-react";
import { Button } from "../ui/button";
import { Progress } from "../ui/progress";
import { Textarea } from "../ui/textarea";
import { toast } from "sonner";
import { localizedScaleLabels } from "../../lib/evaluationScaleLabels";
import PainLocationPicker from "../evaluations/PainLocationPicker";

// Match backend validation: known non-pain metrics never use text inference.
function isPainQuestion(question) {
  if (!question) return false;
  const metric = String(question.metric || "").toLowerCase();
  if (metric) {
    return metric === "pain" || metric === "injury_risk" || question.is_pain_question === true;
  }
  if (question.is_pain_question === true) return true;
  const text = `${question.text || ""} ${String(question.id || "").replace(/_/g, " ")}`;
  return /\b(pain|injury|smärta|ont|skada)\b/i.test(text);
}

// ============================================================================
// Section inference — V1 templates do not have `section_type`. We classify
// each question into development / status / reflection from cues that already
// exist in the data (section text, metric, type). No template is modified.
// ============================================================================
const STATUS_METRICS = new Set([
  "wellbeing",
  "fatigue",
  "pain",
  "motivation",
  "energy",
  "enjoyment",
  "recovery",
  "stress",
  "load",
]);

function isStatusByText(text = "") {
  const t = text.toLowerCase();
  return (
    t.includes("belast") ||
    t.includes("kropp") ||
    t.includes("smärta") ||
    t.includes("trött") ||
    t.includes("ont") ||
    t.includes("mår") ||
    t.includes("hur kände") ||
    t.includes("välmå") ||
    t.includes("status")
  );
}

function inferSectionType(question) {
  if (question.section_type) return question.section_type; // V2 already tagged
  if (question.type === "text") return "reflection";
  const metric = (question.metric || "").toLowerCase();
  if (STATUS_METRICS.has(metric)) return "status";
  if (isStatusByText(question.section) || isStatusByText(question.text)) {
    return "status";
  }
  return "development";
}

const SECTION_ORDER = ["development", "status", "reflection", "meta"];

const SECTION_ICONS = {
  development: Sparkles,
  status: Heart,
  reflection: PenLine,
  meta: Megaphone,
};
const SECTION_CHIP = {
  development: "bg-emerald-500/15 text-[rgb(var(--tn-text-6ee7b7))] border-emerald-500/30",
  status: "bg-sky-500/15 text-[rgb(var(--tn-text-7dd3fc))] border-sky-500/30",
  reflection: "bg-[rgb(var(--tn-bg-71717a))]/15 text-[rgb(var(--tn-text-d4d4d8))] border-[rgb(var(--tn-border-71717a))]/30",
  meta: "bg-amber-500/15 text-[rgb(var(--tn-text-fcd34d))] border-amber-500/30",
};
const SECTION_CARD_BG = {
  development: "bg-card",
  status: "bg-gradient-to-b from-sky-950/30 via-card to-card",
  reflection: "bg-card",
  meta: "bg-gradient-to-b from-amber-950/20 via-card to-card",
};
const SECTION_ACCENT = {
  development: "from-emerald-500/30 to-emerald-600/10",
  status: "from-sky-500/30 to-indigo-500/10",
  reflection: "from-[rgb(var(--tn-bg-71717a))]/30 to-[rgb(var(--tn-bg-52525b))]/10",
  meta: "from-amber-500/30 to-orange-500/10",
};

function buildSectionMeta(t) {
  const one = (key) => ({
    label: t(`development:eval_flow.section_labels.${key}`),
    icon: SECTION_ICONS[key],
    chip: SECTION_CHIP[key],
    cardBg: SECTION_CARD_BG[key],
    accent: SECTION_ACCENT[key],
  });
  return {
    development: one("development"),
    status: one("status"),
    reflection: one("reflection"),
    meta: one("meta"),
  };
}

// ============================================================================
// Age adaptation — affects only visuals & copy, not logic.
// ============================================================================
function ageProfile(template, t) {
  const age = (template?.age_group || "").toUpperCase();
  if (age.startsWith("U8") || age.includes("U8-U11")) {
    return {
      key: "U8-U11",
      tapTarget: "h-14",
      ratingFontSize: "text-base",
      heroFont: "text-2xl",
      sectionIntroTitle: t("development:mobile_flow.u8_11.section_intro_title"),
      doneTitle: t("development:mobile_flow.u8_11.done_title"),
      doneSubtitle: t("development:mobile_flow.u8_11.done_subtitle"),
    };
  }
  if (age.includes("U12") || age.includes("U13") || age.includes("U14") || age.includes("U15")) {
    return {
      key: "U12-U15",
      tapTarget: "h-12",
      ratingFontSize: "text-sm",
      heroFont: "text-2xl",
      sectionIntroTitle: t("development:mobile_flow.u12_15.section_intro_title"),
      doneTitle: t("development:mobile_flow.u12_15.done_title"),
      doneSubtitle: t("development:mobile_flow.u12_15.done_subtitle"),
    };
  }
  return {
    key: "U16+",
    tapTarget: "h-11",
    ratingFontSize: "text-sm",
    heroFont: "text-xl",
    sectionIntroTitle: t("development:mobile_flow.u16_plus.section_intro_title"),
    doneTitle: t("development:mobile_flow.u16_plus.done_title"),
    doneSubtitle: t("development:mobile_flow.u16_plus.done_subtitle"),
  };
}

// ============================================================================
// Visible-question filtering — preserves backend conditional logic exactly.
// ============================================================================
function isQuestionVisible(question, answers) {
  if (!question.parent_question_id) return true;
  const parentAnswer = answers[question.parent_question_id];
  const expected = question.show_if_parent_value;
  if (expected === true) {
    return parentAnswer === "yes" || parentAnswer === true;
  }
  if (expected === false) {
    return parentAnswer === "no" || parentAnswer === false;
  }
  return parentAnswer === expected;
}

// ============================================================================
// Step list builder — preserves backend ordering, only adds intro cards.
// ============================================================================
// Step list builder — preserves backend ordering. NO standalone section-intro
// cards; instead, the first question of each section carries the intro subtitle.
// ============================================================================
function buildSteps(template) {
  const all = (template?.questions || [])
    .slice()
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  const grouped = { development: [], status: [], reflection: [], meta: [] };
  all.forEach((q) => {
    const sec = inferSectionType(q);
    if (grouped[sec]) grouped[sec].push({ ...q, _inferred_section: sec });
  });

  const steps = [{ kind: "hello" }];
  SECTION_ORDER.forEach((sec) => {
    grouped[sec].forEach((q, idx) =>
      steps.push({
        kind: "question",
        section: sec,
        question: q,
        isSectionStart: idx === 0,
      })
    );
  });
  steps.push({ kind: "summary" });
  return steps;
}

function historyAnswers(evaluation, template) {
  const definitions = template?.definition_source === "frozen"
    ? new Map((template.questions || []).map(question => [question.id, question]))
    : new Map();
  return (evaluation?.responses || []).map(response => ({
    id: response.question_id,
    question: definitions.get(response.question_id),
    answer: response.answer,
  }));
}

function formatSavedAnswer(answer) {
  if (answer == null) return "—";
  return typeof answer === "object" ? JSON.stringify(answer) : String(answer);
}

// ============================================================================
// Main component
// ============================================================================
export default function MobileReflectionFlow({
  evaluation,
  onSubmit,
  onClose,
}) {
  const { t, i18n } = useTranslation(["development", "common"]);
  const SECTION_META = useMemo(() => buildSectionMeta(t), [t]);
  const template = useMemo(
    () => ({
      ...(evaluation?.template || {}),
      questions: evaluation?.questions || evaluation?.template?.questions || [],
      definition_source: evaluation?.definition_source || evaluation?.template?.definition_source,
      definition_revision: evaluation?.definition_revision || evaluation?.template?.definition_revision,
      name:
        evaluation?.template_name ||
        evaluation?.template?.name ||
        t("development:mobile_flow.default_template_name"),
      age_group: evaluation?.age_group || evaluation?.template?.age_group,
    }),
    [evaluation, t]
  );
  const readOnly = evaluation?.status === "completed" || Boolean(evaluation?.completed_at);
  const legacyDefinition = template.definition_source !== "frozen";
  const profile = useMemo(() => ageProfile(template, t), [template, t]);
  const steps = useMemo(() => buildSteps(template), [template]);
  const [stepIndex, setStepIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const [answers, setAnswers] = useState(() => {
    if (!evaluation?.responses) return {};
    const out = {};
    evaluation.responses.forEach((r) => {
      out[r.question_id] = r.answer;
    });
    return out;
  });
  const [submitting, setSubmitting] = useState(false);

  // Reset state when a new evaluation is opened.
  useEffect(() => {
    setStepIndex(0);
    setDirection(1);
    if (evaluation?.responses) {
      const out = {};
      evaluation.responses.forEach((r) => {
        out[r.question_id] = r.answer;
      });
      setAnswers(out);
    } else {
      setAnswers({});
    }
  }, [evaluation?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const step = steps[stepIndex];

  // Skip invisible (conditional) questions automatically as we advance.
  useEffect(() => {
    if (step?.kind !== "question") return;
    if (!isQuestionVisible(step.question, answers)) {
      // skip forward without animation
      goNext({ skipped: true });
    }
  }, [stepIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  const visibleQuestions = useMemo(
    () =>
      (template.questions || [])
        .filter((q) => isQuestionVisible(q, answers))
        .map((q) => q.id),
    [template.questions, answers]
  );
  const totalQuestions = visibleQuestions.length;
  const answeredCount = visibleQuestions.filter(
    (qid) => answers[qid] !== undefined && answers[qid] !== ""
  ).length;
  const progressPct =
    totalQuestions === 0
      ? 0
      : Math.min(100, Math.round((answeredCount / totalQuestions) * 100));
  const currentQuestionIndex =
    step?.kind === "question"
      ? visibleQuestions.indexOf(step.question.id) + 1
      : null;

  function goNext({ skipped } = {}) {
    if (stepIndex < steps.length - 1) {
      setDirection(1);
      setStepIndex(stepIndex + (skipped ? 1 : 1));
    }
  }
  function goBack() {
    if (stepIndex > 0) {
      setDirection(-1);
      setStepIndex(stepIndex - 1);
    }
  }
  function setAnswer(qid, value) {
    if (readOnly) return;
    setAnswers((prev) => ({ ...prev, [qid]: value }));
  }

  // Auto-advance after a rating tap. RatingScale already debounces the
  // confirmation with ~250ms so the active state registers visually.
  function autoAdvanceAfterRating(qid, value) {
    setAnswer(qid, value);
    goNext();
  }

  const canAdvance = (() => {
    if (step?.kind !== "question") return true;
    const q = step.question;
    if (q.required === false) return true;
    if (q.type === "text") return true; // text always optional in this flow
    const v = answers[q.id];
    return v !== undefined && v !== null && v !== "";
  })();

  async function handleSubmit() {
    if (readOnly) return;
    // Preserve backend required-question validation exactly.
    const visibleRequired = (template.questions || []).filter((q) => {
      if (q.required === false) return false;
      if (!isQuestionVisible(q, answers)) return false;
      if (q.type === "text") return false;
      return true;
    });
    const missing = visibleRequired.filter(
      (q) => answers[q.id] === undefined || answers[q.id] === ""
    );
    if (missing.length > 0) {
      toast.error(t("development:mobile_flow.required_toast"));
      // jump back to first unanswered
      const firstId = missing[0].id;
      const idx = steps.findIndex(
        (s) => s.kind === "question" && s.question.id === firstId
      );
      if (idx >= 0) {
        setDirection(-1);
        setStepIndex(idx);
      }
      return;
    }
    // Build responses. The `__pain_loc` synthetic key holds the inline
    // pain follow-up (area + side + duration) — expand it to discrete
    // structured responses so the backend can write a pain_event row.
    const responses = [];
    Object.entries(answers).forEach(([qid, ans]) => {
      if (qid.endsWith("__pain_loc")) {
        const baseQid = qid.replace("__pain_loc", "");
        const loc = ans || {};
        if (loc?.area) {
          responses.push({
            question_id: `${baseQid}__pain_area`,
            answer: loc.area,
            metric: "pain_location",
            core_metric: "load",
            section_type: "status",
          });
          if (loc.side) {
            responses.push({
              question_id: `${baseQid}__pain_side`,
              answer: loc.side,
              metric: "pain_side",
              core_metric: "load",
              section_type: "status",
            });
          }
          if (loc.duration) {
            responses.push({
              question_id: `${baseQid}__pain_duration`,
              answer: loc.duration,
              metric: "pain_duration",
              core_metric: "load",
              section_type: "status",
            });
          }
        }
        return;
      }
      responses.push({ question_id: qid, answer: ans });
    });
    try {
      setSubmitting(true);
      await onSubmit(evaluation.id, responses, template.definition_revision);
      // Toast handled by parent on success.
    } catch (err) {
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  }

  if (!evaluation) return null;

  const definitionNotice = legacyDefinition && (
    <p className="text-xs text-muted-foreground" role="note">
      {t("development:history.definition_missing", { defaultValue: i18n.language?.startsWith("en")
        ? "The original question version was not saved. The wording shown may be from a later template."
        : "Den ursprungliga frågeversionen sparades inte. Frågorna som visas kan komma från en senare mall." })}
    </p>
  );

  // Completed evaluations are historical records, never an editable reflection flow.
  if (readOnly) {
    const saved = historyAnswers(evaluation, template);
    return (
      <div data-testid="mobile-reflection-history"
        className="fixed inset-0 z-50 flex flex-col bg-background"
        style={{ paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" }}>
        <header className="px-5 pt-3 pb-4 flex items-center gap-3">
          <button type="button" aria-label={t("development:mobile_flow.close")} onClick={onClose}
            className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-muted/30">
            <X className="w-5 h-5 text-muted-foreground" />
          </button>
          <div className="min-w-0 flex-1">
            <h1 className="text-sm font-semibold truncate">{template.name}</h1>
            <ObservationDate period={evaluation.observation_period} />
            <p className="text-xs text-muted-foreground">{t("development:history.saved_answers", {
              defaultValue: i18n.language?.startsWith("en") ? "Saved answers" : "Sparade svar" })}</p>
          </div>
        </header>
        <main className="flex-1 min-h-0 overflow-y-auto px-5 pb-6 space-y-4">
          {definitionNotice}
          {saved.map(({ id, question, answer }) => {
            const scaleMax = question?.scale_max || (question?.type === "rating_10" ? 10 : ["rating_5", "rating", "emoji_5"].includes(question?.type) ? 5 : null);
            const labels = localizedScaleLabels(question?.scale_labels, i18n.language, "frozen");
            return <div key={id} className="rounded-2xl bg-card border border-border p-4 space-y-2">
              <p className="text-sm font-medium">{question?.text || id}</p>
              {question?.scale_description && <p className="text-xs text-muted-foreground">{question.scale_description}</p>}
              <p className="text-sm whitespace-pre-wrap">{formatSavedAnswer(answer)}{typeof answer === "number" && scaleMax ? `/${scaleMax}` : ""}</p>
              {labels && <div className="flex justify-between gap-4 text-xs text-muted-foreground"><span>{labels.left}</span><span>{labels.right}</span></div>}
            </div>;
          })}
          {!saved.length && <p className="text-sm text-muted-foreground">{t("development:history.no_saved_answers", {
            defaultValue: i18n.language?.startsWith("en") ? "No saved answers." : "Inga sparade svar." })}</p>}
        </main>
      </div>
    );
  }

  return (
    <div
      data-testid="mobile-reflection-flow"
      className="fixed inset-0 z-50 flex flex-col bg-background"
      style={{
        paddingTop: "env(safe-area-inset-top)",
        paddingBottom: "env(safe-area-inset-bottom)",
      }}
    >
      {/* Header */}
      <header className="px-5 pt-3 pb-2 flex items-center gap-3">
        <button
          type="button"
          aria-label={t("development:mobile_flow.close")}
          onClick={onClose}
          data-testid="reflection-close"
          className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-muted/30 active:scale-95 transition"
        >
          <X className="w-5 h-5 text-muted-foreground" />
        </button>
        <div className="flex-1 min-w-0">
          <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">
            {currentQuestionIndex
              ? t("development:mobile_flow.question_progress", {
                  current: currentQuestionIndex,
                  total: totalQuestions,
                })
              : t("development:mobile_flow.reflection_label")}
          </p>
          <h1
            className="text-sm font-semibold text-[rgb(var(--tn-text-ffffff))] truncate"
            data-testid="reflection-title"
          >
            {template.name}
          </h1>
          <ObservationDate period={evaluation.observation_period} />
        </div>
      </header>

      {definitionNotice && <div className="px-5 pb-3">{definitionNotice}</div>}

      {/* Progress */}
      <div className="px-5 pb-3">
        <Progress
          value={progressPct}
          className="h-1.5 bg-muted/30"
          data-testid="reflection-progress"
        />
      </div>

      {/* Card stage */}
      <main className="flex-1 px-5 pb-28 overflow-y-auto flex items-start justify-center">
        <div className="w-full max-w-md">
          <AnimatePresence mode="wait" custom={direction} initial={false}>
            <motion.div
              key={stepIndex}
              custom={direction}
              initial={{ x: direction > 0 ? 40 : -40, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: direction > 0 ? -40 : 40, opacity: 0 }}
              transition={{ type: "spring", stiffness: 280, damping: 26 }}
              className="w-full"
            >
              {step?.kind === "hello" && (
                <HelloCard
                  template={template}
                  profile={profile}
                  totalQuestions={totalQuestions}
                  onStart={goNext}
                  t={t}
                />
              )}

              {step?.kind === "question" && (
                <QuestionCard
                  question={{ ...step.question, definition_source: step.question.definition_source || template.definition_source }}
                  section={step.section}
                  isSectionStart={!!step.isSectionStart}
                  value={answers[step.question.id]}
                  onChange={(v) => setAnswer(step.question.id, v)}
                  onAutoAdvance={(v) =>
                    autoAdvanceAfterRating(step.question.id, v)
                  }
                  painLocation={answers[`${step.question.id}__pain_loc`]}
                  onPainLocationChange={(loc) =>
                    setAnswer(`${step.question.id}__pain_loc`, loc)
                  }
                  profile={profile}
                  sectionMeta={SECTION_META}
                  t={t}
                />
              )}

              {step?.kind === "summary" && (
                <SummaryCard
                  profile={profile}
                  submitting={submitting}
                  onSubmit={handleSubmit}
                  totalAnswered={answeredCount}
                  total={totalQuestions}
                  t={t}
                />
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </main>

      {/* Footer nav (sticky pill bar) */}
      <footer
        className="fixed inset-x-0 bottom-0 backdrop-blur bg-background/80 border-t border-border px-5 py-3 flex items-center gap-3"
        style={{ paddingBottom: "calc(12px + env(safe-area-inset-bottom))" }}
      >
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={goBack}
          disabled={stepIndex === 0}
          data-testid="reflection-back"
          className="rounded-full"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          {t("development:mobile_flow.back")}
        </Button>
        <div className="flex-1" />
        {step?.kind !== "summary" && step?.kind !== "question" && (
          <Button
            type="button"
            onClick={() => goNext()}
            data-testid="reflection-next"
            className="rounded-full px-6 bg-primary text-primary-foreground"
          >
            {step?.kind === "hello"
              ? t("development:mobile_flow.start")
              : t("development:mobile_flow.continue")}
            <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        )}
        {step?.kind === "question" && (
          <Button
            type="button"
            onClick={() => goNext()}
            disabled={!canAdvance}
            data-testid="reflection-next"
            className="rounded-full px-6 bg-primary text-primary-foreground"
          >
            {t("development:mobile_flow.next")}
            <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        )}
      </footer>
    </div>
  );
}

// ============================================================================
// HelloCard — first card the player sees.
// ============================================================================
function HelloCard({ template, profile, totalQuestions, onStart, t }) {
  return (
    <div
      data-testid="reflection-hello"
      className="rounded-3xl bg-card border border-border shadow-[0_8px_30px_rgba(0,0,0,0.06)] p-7 text-center"
    >
      <div className="mx-auto w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mb-5">
        <Sparkles className="w-7 h-7 text-primary" />
      </div>
      <h2 className={`${profile.heroFont} font-semibold text-[rgb(var(--tn-text-ffffff))]`}>
        {template.name}
      </h2>
      <p className="mt-3 text-sm text-muted-foreground max-w-xs mx-auto leading-relaxed">
        {profile.key === "U8-U11"
          ? t("development:mobile_flow.hello_subtitle_u8_11", { count: totalQuestions })
          : t("development:mobile_flow.hello_subtitle_default", { count: totalQuestions })}
      </p>
      <Button
        type="button"
        onClick={onStart}
        data-testid="reflection-hello-start"
        className="mt-7 w-full h-12 rounded-full bg-primary text-primary-foreground text-base"
      >
        {profile.sectionIntroTitle}
        <ArrowRight className="w-4 h-4 ml-1" />
      </Button>
    </div>
  );
}

// ============================================================================
// (SectionIntroCard removed — section transitions are now seamless. The first
// question of each section carries the intro subtitle.)
// ============================================================================

// ============================================================================
// Section intro copy — rendered as a subtitle on the FIRST question of each
// section. NEVER as a dead "Fortsätt" screen. Tone is calm, modern, human.
// Localized via i18n; only the STATUS section shows an inline title.
// ============================================================================
function getSectionIntroCopy(section, t) {
  return {
    title:
      section === "status" ? t("development:mobile_flow.status_section_title") : null,
    body: t(`development:eval_flow.section_intros.${section}`),
  };
}

// ============================================================================
// QuestionCard — one hero card per question, auto-advance after rating tap.
// ============================================================================
function QuestionCard({
  question,
  section,
  isSectionStart,
  value,
  onChange,
  onAutoAdvance,
  painLocation,
  onPainLocationChange,
  profile,
  sectionMeta,
  t,
}) {
  const meta = sectionMeta[section];
  const Icon = meta.icon;
  const isStatus = section === "status";
  const intro = getSectionIntroCopy(section, t);

  // Pain must not be inferred from generic load/fatigue or parts of words.
  const isPain = isPainQuestion(question);
  const isFatigue =
    (question.metric || "").toLowerCase() === "fatigue" ||
    (question.text || "").toLowerCase().includes("trött");
  const inverted = Boolean(question.scale_inverted || isPain || isFatigue);
  const showPainFollowup = isPain && typeof value === "number" && value >= 7;

  // Wrap auto-advance: never advance straight after a high-pain answer —
  // the player should still pick area / side / duration first.
  const guardedAutoAdvance = (v) => {
    if (isPain && typeof v === "number" && v >= 7) return;
    onAutoAdvance?.(v);
  };

  return (
    <div
      data-testid={`reflection-question-${question.id}`}
      className={`relative overflow-hidden rounded-3xl border border-border shadow-[0_8px_30px_rgba(0,0,0,0.06)] p-7 ${meta.cardBg}`}
    >
      <div className="flex items-center gap-2 mb-4">
        <span
          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-medium uppercase tracking-wide ${meta.chip}`}
        >
          <Icon className="w-3 h-3" />
          {meta.label}
        </span>
      </div>

      {/* Section subtitle — replaces the old standalone intro screen. */}
      {isSectionStart && intro && (
        <div
          data-testid={`reflection-section-intro-${section}`}
          className="mb-3"
        >
          {intro.title && (
            <h3 className="text-base font-medium text-[rgb(var(--tn-text-ffffff))]/90 leading-snug">
              {intro.title}
            </h3>
          )}
          <p className="text-xs text-muted-foreground leading-relaxed mt-0.5">
            {intro.body}
          </p>
        </div>
      )}

      <h2
        className={`${profile.heroFont} font-semibold text-[rgb(var(--tn-text-ffffff))] leading-snug`}
        data-testid={`reflection-question-text-${question.id}`}
      >
        {question.text}
      </h2>

      {question.scale_description && (
        <p className="mt-2 text-xs text-muted-foreground">
          {question.scale_description}
        </p>
      )}

      <div className="mt-7">
        {(question.type === "rating_10" || question.type === "rating10") && (
          <RatingScale
            max={10}
            value={value}
            onChange={onChange}
            onConfirm={guardedAutoAdvance}
            qid={question.id}
            inverted={inverted}
            isStatus={isStatus}
            tapTarget={profile.tapTarget}
            fontSize={profile.ratingFontSize}
            question={question}
            t={t}
          />
        )}

        {(question.type === "rating_5" ||
          question.type === "rating" ||
          question.type === "rating5") && (
          <RatingScale
            max={5}
            value={value}
            onChange={onChange}
            onConfirm={guardedAutoAdvance}
            qid={question.id}
            inverted={inverted}
            isStatus={isStatus}
            tapTarget={profile.tapTarget}
            fontSize={profile.ratingFontSize}
            question={question}
            t={t}
          />
        )}

        {question.type === "emoji_5" && (
          <EmojiScale
            value={value}
            onChange={onChange}
            onConfirm={guardedAutoAdvance}
            qid={question.id}
            t={t}
          />
        )}

        {question.type === "yes_no" && (
          <YesNoToggle
            value={value}
            onChange={onChange}
            onConfirm={guardedAutoAdvance}
            qid={question.id}
            t={t}
          />
        )}

        {question.allow_not_applicable && (
          <button type="button" aria-pressed={value === "not_applicable"}
            className={`mt-3 w-full rounded-xl border p-3 text-sm ${value === "not_applicable" ? "border-primary text-primary" : "border-border text-muted-foreground"}`}
            onClick={() => onChange("not_applicable")}>
            {t('development:question_not_applicable')}
          </button>
        )}
        {question.type === "text" && (
          <Textarea
            data-testid={`reflection-text-${question.id}`}
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder={
              profile.key === "U8-U11"
                ? t("development:mobile_flow.text_placeholder_u8_11")
                : t("development:mobile_flow.text_placeholder_default")
            }
            rows={4}
            className="rounded-2xl text-base resize-none"
          />
        )}
      </div>

      {showPainFollowup && (
        <div className="mt-6">
          <PainLocationPicker
            value={painLocation}
            onChange={onPainLocationChange}
          />
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Scale label inference — backend may later send `question.scale_labels`
// ({left, right}). Until then we infer localized anchors from question metric
// and text (kept language-neutral by mapping to i18n keys).
// ============================================================================
function inferScaleLabels(question, { isStatus, inverted }, t) {
  const pair = (key) => ({
    left: t(`development:mobile_flow.rating.labels.${key}.left`),
    right: t(`development:mobile_flow.rating.labels.${key}.right`),
  });

  // 1. Explicit override from backend wins.
  const explicit = localizedScaleLabels(question?.scale_labels, t("development:template_scale_language", { defaultValue: "sv" }), question?.definition_source);
  if (explicit && (explicit.left || explicit.right)) {
    return { left: explicit.left || "", right: explicit.right || "" };
  }

  // Missing historical anchors must not be replaced by today's inferred wording.
  if (question?.definition_source === "frozen") return { left: "", right: "" };
  const text = (question?.text || "").toLowerCase();
  const metric = (question?.metric || "").toLowerCase();

  // Detection cues are kept in Swedish AND English (some V1 templates still
  // use Swedish scale hints; V2 uses metric-only or explicit scale_labels).
  const hasAny = (arr) => arr.some((s) => text.includes(s));

  // 2. Status-specific anchors.
  if (isStatus) {
    if (metric === "pain" || hasAny(["smärta", "ont", "pain"])) return pair("pain");
    if (metric === "fatigue" || hasAny(["trött", "kropp", "tired", "body"])) return pair("fatigue");
    if (metric === "wellbeing" || hasAny(["mår", "känn", "feel", "wellbeing"])) return pair("wellbeing");
    if (metric === "motivation" || hasAny(["motiverad", "motivated"])) return pair("motivation");
    if (inverted) return pair("inverted");
    return pair("default_status");
  }

  // 3. Development anchors — pick from question wording (SV + EN cues).
  if (hasAny(["nöjd", "satisfied"])) return pair("satisfaction");
  if (hasAny(["trygg", "safe"])) return pair("safety");
  if (hasAny(["svår", "hard", "difficult"])) return pair("difficulty");
  if (hasAny(["säker", "confident"])) return pair("confidence");
  if (
    text.startsWith("jag ") ||
    text.startsWith("i ") ||
    hasAny([
      "ofta", "kämp", "press", "höll", "vågade", "jobbade",
      "often", "fought", "held", "dared", "worked",
    ])
  ) {
    return pair("frequency");
  }
  return pair("default");
}

// ============================================================================
// RatingScale — neutral surface, gold active for development, sky for status.
// 1-10 uses TWO rows (1-5 / 6-10) for larger touch targets and calmer feel.
// 1-5 uses a single row. Auto-confirms after ~250ms.
// ============================================================================
function RatingScale({
  max = 10,
  value,
  onChange,
  onConfirm,
  qid,
  inverted = false,
  isStatus = false,
  tapTarget = "h-12",
  fontSize = "text-sm",
  question,
  t,
}) {
  const items = Array.from({ length: max }, (_, i) => i + 1);
  const rows = max === 10 ? [items.slice(0, 5), items.slice(5, 10)] : [items];
  const { left, right } = inferScaleLabels(
    question || {},
    { isStatus, inverted },
    t
  );

  // Surface colors — neutral dark by default. Active uses gold for development
  // and sky (or amber if inverted) for status to make wellbeing feel calmer
  // and never read as "score".
  const activeClass = isStatus
    ? inverted
      ? "bg-amber-500/90 border-amber-400 text-[rgb(var(--tn-text-ffffff))] shadow-[0_0_18px_rgba(251,191,36,0.35)]"
      : "bg-sky-500/90 border-sky-400 text-[rgb(var(--tn-text-ffffff))] shadow-[0_0_18px_rgba(56,189,248,0.35)]"
    : "bg-primary border-primary text-primary-foreground shadow-[0_0_18px_rgba(212,175,55,0.35)]";
  const idleClass =
    "bg-muted/20 border-border/70 text-muted-foreground hover:border-primary/40 hover:bg-muted/30";

  // Larger pills when scale is 5 (and on U8-U11 caps). For 10 they stay tap-friendly
  // through the 2-row layout — every button is ~63px wide on a 390px viewport.
  const radius = max === 5 ? "rounded-2xl" : "rounded-xl";

  return (
    <div data-testid={`rating-scale-${qid}`} className="w-full">
      <div className="space-y-2">
        {rows.map((row, rowIdx) => (
          <div
            key={rowIdx}
            className={`grid grid-cols-5 gap-2`}
            data-testid={`rating-scale-${qid}-row-${rowIdx}`}
          >
            {row.map((n) => {
              const active = value === n;
              return (
                <button
                  key={n}
                  type="button"
                  data-testid={`rating-${qid}-${n}`}
                  aria-label={t("development:mobile_flow.rating.aria", { n, max })}
                  aria-pressed={active}
                  onClick={() => {
                    onChange(n);
                    if (onConfirm) {
                      // ~250ms feels fast but lets the active state register.
                      setTimeout(() => onConfirm(n), 250);
                    }
                  }}
                  className={`${tapTarget} ${radius} ${fontSize} font-semibold transition-all duration-150 active:scale-95 border ${
                    active ? activeClass : idleClass
                  }`}
                >
                  {n}
                </button>
              );
            })}
          </div>
        ))}
      </div>
      {(left || right) && (
        <div className="mt-3 flex justify-between text-[11px] text-muted-foreground px-1">
          <span>{left}</span>
          <span>{right}</span>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// EmojiScale — STATUS picker for U8-U11 (kept compatible with emoji_5).
// Labels are looked up from i18n so they follow the user's locale.
// ============================================================================
const FACE_CHARS = { 1: "😞", 2: "😟", 3: "😐", 4: "🙂", 5: "😍" };

function EmojiScale({ value, onChange, onConfirm, qid, t }) {
  const faces = [1, 2, 3, 4, 5].map((v) => ({
    value: v,
    char: FACE_CHARS[v],
    label: t(`development:mobile_flow.emoji.labels.${v}`),
  }));
  return (
    <div data-testid={`emoji-scale-${qid}`} className="w-full">
      <div className="grid grid-cols-5 gap-2">
        {faces.map((f) => {
          const active = value === f.value;
          return (
            <button
              key={f.value}
              type="button"
              data-testid={`emoji-${qid}-${f.value}`}
              aria-label={f.label}
              aria-pressed={active}
              onClick={() => {
                onChange(f.value);
                if (onConfirm) setTimeout(() => onConfirm(f.value), 250);
              }}
              className={`relative h-20 rounded-3xl border text-4xl flex items-center justify-center transition-all duration-200 active:scale-95 ${
                active
                  ? "bg-amber-500/20 border-amber-400 scale-105"
                  : "bg-muted/30 border-border grayscale-[40%]"
              }`}
            >
              <span>{f.char}</span>
            </button>
          );
        })}
      </div>
      <p className="mt-3 text-center text-xs text-muted-foreground">
        {value
          ? faces.find((f) => f.value === value)?.label
          : t("development:mobile_flow.emoji.pick_prompt")}
      </p>
    </div>
  );
}

// ============================================================================
// YesNoToggle — preserves yes_no question type.
// ============================================================================
function YesNoToggle({ value, onChange, onConfirm, qid, t }) {
  const opts = [
    { v: "yes", label: t("development:mobile_flow.yes") },
    { v: "no", label: t("development:mobile_flow.no") },
  ];
  return (
    <div className="grid grid-cols-2 gap-3" data-testid={`yesno-${qid}`}>
      {opts.map((o) => {
        const active = value === o.v || (o.v === "yes" && value === true);
        return (
          <button
            key={o.v}
            type="button"
            data-testid={`yesno-${qid}-${o.v}`}
            onClick={() => {
              onChange(o.v);
              if (onConfirm) setTimeout(() => onConfirm(o.v), 250);
            }}
            className={`h-14 rounded-2xl font-semibold transition border ${
              active
                ? "bg-primary border-primary text-primary-foreground"
                : "bg-muted/30 border-border text-muted-foreground"
            }`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

// ============================================================================
// SummaryCard — lightweight completion + submit.
// ============================================================================
function SummaryCard({ profile, submitting, onSubmit, totalAnswered, total, t }) {
  return (
    <div
      data-testid="reflection-summary"
      className="rounded-3xl bg-card border border-border shadow-[0_8px_30px_rgba(0,0,0,0.06)] p-8 text-center"
    >
      <div className="mx-auto w-14 h-14 rounded-full bg-emerald-500/15 flex items-center justify-center mb-5">
        <Check className="w-7 h-7 text-[rgb(var(--tn-text-34d399))]" />
      </div>
      <h2 className={`${profile.heroFont} font-semibold text-[rgb(var(--tn-text-ffffff))]`}>
        {profile.doneTitle}
      </h2>
      <p className="mt-3 text-sm text-muted-foreground max-w-xs mx-auto leading-relaxed">
        {profile.doneSubtitle}
      </p>
      <div className="mt-5 inline-flex items-center gap-1.5 text-xs text-muted-foreground">
        {t("development:mobile_flow.answered_progress", { answered: totalAnswered, total })}
      </div>
      <Button
        type="button"
        onClick={onSubmit}
        disabled={submitting}
        data-testid="reflection-submit"
        className="mt-8 w-full h-12 rounded-full bg-primary text-primary-foreground text-base"
      >
        {submitting ? t("development:mobile_flow.saving") : t("development:mobile_flow.submit")}
      </Button>
    </div>
  );
}
